import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { FaUser, FaPhone, FaEnvelope, FaBell } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import Button from './ui/Button';
import ConfirmDialog from './ConfirmDialog';
import { Input } from './pilot-ui/input';
import { Label } from './pilot-ui/label';
import { Textarea } from './pilot-ui/textarea';
import { Link } from 'react-router';
import AppUserSearchActivity from './AppUserDetails/AppUserSearchActivity';
import { useUnreadNoteCount } from '../hooks/useUnreadNoteCount';
import { readOnlyUnread, saveOnlyUnread } from './AppUserDetails/appUsersPrefs';

interface User {
  uid: string;
  email?: string | null;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  userType?: string;
  isPhoneVerified?: boolean;
  hasSetPassword?: boolean;
  isSetupDone?: boolean;
  hasPushToken: boolean;
  notesContactConsentEffective: boolean;
  createdAt: string | null;
}

interface UserPage {
  items: User[];
  nextCursor: string | null;
}

interface PageRequest {
  index: number;
  cursor: string | null;
}

interface NotificationData {
  title: string;
  body: string;
  data?: Record<string, string>;
}

interface NotificationResponse {
  success: boolean;
  messageId: string;
}

const SEND_REASON = 'Sending push notifications requires the send_notifications permission.';
const PAGE_SIZE = 50;

const AppUsers: React.FC = () => {
  const { id: accountId, hasPermission, scopes } = useAuth();
  // FS-906: the list is every app account, so reading it needs firestore_read
  // and the full device scope '/', the rule firebase-api enforces. Sending is
  // its own permission, send_notifications.
  const readBlockedReason = !hasPermission('firestore_read')
    ? 'Viewing App Users requires the firestore_read permission.'
    : !scopes.includes('/')
      ? 'Viewing App Users requires full device scope (/).'
      : null;
  const canRead = readBlockedReason === null;
  const canSend = canRead && hasPermission('send_notifications');
  const { error: unreadCountError } = useUnreadNoteCount();
  const [onlyUnread, setOnlyUnread] = useState(readOnlyUnread);

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageCursors, setPageCursors] = useState<(string | null)[]>([null]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [failedPage, setFailedPage] = useState<PageRequest | null>(null);
  const listRequest = useRef(0);

  const [notificationModal, setNotificationModal] = useState<{ open: boolean, user?: User }>({ open: false });
  const [notification, setNotification] = useState<NotificationData>({ title: '', body: '' });
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationResult, setNotificationResult] = useState<{ success: boolean, message: string } | null>(null);
  const [missingNotification, setMissingNotification] = useState({ title: false, body: false });
  // Counts composer openings and closings. A send's reply is applied only while
  // the composer it was sent from is still the open one.
  const composerRef = useRef(0);

  const fetchPage = async (page: PageRequest) => {
    const request = ++listRequest.current;
    setLoading(true);
    setError(null);
    setFailedPage(null);
    try {
      const response = await axiosInstance.get<UserPage>(`${config.firebaseApiUrl}/fb/homeowners`, {
        params: {
          limit: PAGE_SIZE,
          ...(page.cursor ? { cursor: page.cursor } : {}),
          ...(onlyUnread ? { unread: true } : {}),
        },
      });
      if (request !== listRequest.current) return;
      setUsers(response.data.items);
      setPageIndex(page.index);
      setNextCursor(response.data.nextCursor);
      setPageCursors(previous => {
        const cursors = [...previous];
        cursors[page.index] = page.cursor;
        if (response.data.nextCursor) cursors[page.index + 1] = response.data.nextCursor;
        return cursors;
      });
    } catch (err) {
      if (request !== listRequest.current) return;
      console.error('Error fetching users:', err);
      setError('Failed to fetch users. Please try again later.');
      setFailedPage(page);
    } finally {
      if (request === listRequest.current) setLoading(false);
    }
  };

  useEffect(() => {
    // A reply that lands after the account or its access changed is dropped.
    listRequest.current += 1;
    setUsers([]);
    setError(null);
    setLoading(false);
    setPageIndex(0);
    setPageCursors([null]);
    setNextCursor(null);
    setFailedPage(null);
    if (canRead) void fetchPage({ index: 0, cursor: null });
    return () => { listRequest.current += 1; };
  }, [canRead, accountId, onlyUnread]);

  const changeOnlyUnread = (value: boolean) => {
    setOnlyUnread(value);
    saveOnlyUnread(value);
  };

  // A change of account or of the send permission closes the composer.
  useEffect(() => {
    composerRef.current += 1;
    setNotificationModal({ open: false });
  }, [accountId, canSend]);

  const formatPhoneNumber = (phone?: string) => {
    if (!phone) return '';

    // Handle E.164 format (e.g., +13302330093)
    if (phone.startsWith('+1') && phone.length === 12) {
      const areaCode = phone.substring(2, 5);
      const middle = phone.substring(5, 8);
      const last = phone.substring(8);
      return `(${areaCode}) ${middle}-${last}`;
    }

    return phone;
  };

  const getUserFullName = (user: User) => {
    const first = user.firstName || '';
    const last = user.lastName || '';

    if (first || last) {
      return `${first} ${last}`.trim();
    }

    return 'N/A';
  };

  const getUserIdentity = (user: User) => {
    const name = getUserFullName(user);
    return user.email?.trim() || (name === 'N/A' ? user.uid : name);
  };

  const openNotificationModal = (user: User) => {
    composerRef.current += 1;
    setNotificationModal({ open: true, user });
    setNotification({ title: '', body: '' });
    setNotificationResult(null);
    setMissingNotification({ title: false, body: false });
    setSendingNotification(false);
  };

  const closeNotificationModal = () => {
    composerRef.current += 1;
    setNotificationModal({ open: false });
  };

  const handleNotificationChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNotification(prev => ({ ...prev, [name]: value }));
    setMissingNotification(prev => ({ ...prev, [name]: false }));
  };

  const sendNotification = async () => {
    if (!notificationModal.user) return;
    const titleMissing = !notification.title.trim();
    const bodyMissing = !notification.body.trim();
    if (titleMissing || bodyMissing) {
      setMissingNotification({ title: titleMissing, body: bodyMissing });
      return;
    }
    const composer = composerRef.current;

    try {
      setSendingNotification(true);
      setNotificationResult(null);

      // Prepare the request payload
      const payload = {
        uid: notificationModal.user.uid,
        title: notification.title,
        body: notification.body
      };

      // Send the notification
      const response = await axiosInstance.post<NotificationResponse>(`${config.firebaseApiUrl}/fb/users/notify`, payload);
      if (composerRef.current !== composer) return;

      setNotificationResult({
        success: true,
        message: `Notification sent successfully! Message ID: ${response.data.messageId}`
      });

      // Clear the form after successful send
      setNotification({ title: '', body: '' });

    } catch (err) {
      if (composerRef.current !== composer) return;
      const axiosError = err as { response?: { data?: { message?: string } } };
      setNotificationResult({
        success: false,
        message: axiosError.response?.data?.message || 'Failed to send notification. Please try again.'
      });
    } finally {
      if (composerRef.current === composer) setSendingNotification(false);
    }
  };

  const pagingControls = (position: 'above' | 'below') => (
    <div role="group" aria-label={`App users page controls ${position} table`} className="my-4 flex items-center gap-3">
      <Button
        variant="secondary"
        disabled={loading || pageIndex === 0}
        onClick={() => void fetchPage({ index: pageIndex - 1, cursor: pageCursors[pageIndex - 1] })}
        title={loading ? 'Wait for the app users page to load' : pageIndex === 0 ? 'Already on the first page of app users' : 'Show the previous page of app users'}
      >Previous</Button>
      <span>Page {pageIndex + 1} · {users.length ? `rows ${pageIndex * PAGE_SIZE + 1}–${pageIndex * PAGE_SIZE + users.length}` : 'no rows'}</span>
      <Button
        variant="secondary"
        disabled={loading || nextCursor === null}
        onClick={() => nextCursor && void fetchPage({ index: pageIndex + 1, cursor: nextCursor })}
        title={loading ? 'Wait for the app users page to load' : nextCursor === null ? 'Already on the last page of app users' : 'Show the next page of app users'}
      >Next</Button>
    </div>
  );

  if (!canRead) {
    return (
      <div className="container mx-auto p-6">
        <h1 className="text-2xl font-bold mb-4">App Users</h1>
        <div className="bg-gray-50 border border-border text-gray-700 p-4 rounded-md">
          <p>{readBlockedReason}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold mb-4">App Users</h1>

      <AppUserSearchActivity
        onlyUnread={onlyUnread}
        onOnlyUnreadChange={changeOnlyUnread}
        unreadCountError={unreadCountError}
      />

      {!canSend && <p className="text-sm text-muted-foreground mb-4">{SEND_REASON}</p>}

      {loading && users.length === 0 && <p role="status" className="text-muted-foreground">Loading users...</p>}

      {error && (
        <div role="alert" className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <p>{error}</p>
          {failedPage && <Button variant="secondary" onClick={() => void fetchPage(failedPage)} title="Retry loading this page of app users">Retry</Button>}
        </div>
      )}

      {!loading && !error && users.length === 0 && pageIndex === 0 && (
        <p className="text-muted-foreground">{onlyUnread ? 'No one has unread notes.' : 'No users found.'}</p>
      )}

      {(users.length > 0 || pageIndex > 0) && pagingControls('above')}

      {!loading && !error && users.length === 0 && pageIndex > 0 && (
        <p className="text-muted-foreground">No more app users after the previous page.</p>
      )}

      {users.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-border">
            <thead>
              <tr className="bg-gray-100">
                <th className="py-2 px-4 border-b text-left">Email</th>
                <th className="py-2 px-4 border-b text-left">Name</th>
                <th className="py-2 px-4 border-b text-left">Phone</th>
                <th className="py-2 px-4 border-b text-left">User Type</th>
                <th className="py-2 px-4 border-b text-left">Status</th>
                {canSend && <th className="py-2 px-4 border-b text-left">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={canSend ? 6 : 5} role="status" className="py-2 px-4 border-b">Loading app users page…</td></tr>}
              {users.map(user => {
                return (
                <tr
                  key={user.uid}
                  className="hover:bg-gray-50"
                >
                  <td className="py-2 px-4 border-b">
                    <div className="flex items-center">
                      <FaEnvelope className="text-muted-foreground mr-2" />
                      <Link
                        to={`/app-users/${encodeURIComponent(user.uid)}`}
                        className="text-primary underline"
                        title={`Open ${getUserIdentity(user)}'s app user account and notes`}
                      >
                        {user.email || 'N/A'}
                      </Link>
                    </div>
                  </td>
                  <td className="py-2 px-4 border-b">
                    <div className="flex items-center">
                      <FaUser className="text-muted-foreground mr-2" />
                      {getUserFullName(user)}
                    </div>
                  </td>
                  <td className="py-2 px-4 border-b">
                    <div className="flex items-center">
                      <FaPhone className="text-muted-foreground mr-2" />
                      {formatPhoneNumber(user.phoneNumber) || 'N/A'}
                      {user.isPhoneVerified && (
                        <span className="ml-2 border border-status-success-border bg-status-success text-status-success-foreground text-xs px-2 py-1 rounded-sm">Verified</span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-4 border-b">
                    {user.userType || 'N/A'}
                  </td>
                  <td className="py-2 px-4 border-b">
                    <div className="flex flex-col space-y-1">
                      {user.hasSetPassword && (
                        <span className="border border-status-info-border bg-status-info text-status-info-foreground text-xs px-2 py-1 rounded-sm inline-block w-fit">Password Set</span>
                      )}
                      {user.isSetupDone && (
                        <span className="border border-status-success-border bg-status-success text-status-success-foreground text-xs px-2 py-1 rounded-sm inline-block w-fit">Setup Complete</span>
                      )}
                      {user.hasPushToken && (
                        <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-sm inline-block w-fit">Has FCM Token</span>
                      )}
                    </div>
                  </td>
                  {canSend && (
                    <td className="py-2 px-4 border-b">
                      <Button
                        variant="primary"
                        onClick={() => openNotificationModal(user)}
                        disabled={!user.hasPushToken}
                        title={
                          user.hasPushToken
                            ? `Open a form to send a push notification to ${getUserIdentity(user)} through Firebase`
                            : "This user's app has not registered for push notifications, so nothing can be sent"
                        }
                      >
                        <FaBell />
                        Push Msg
                      </Button>
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(users.length > 0 || pageIndex > 0) && pagingControls('below')}

      {canSend && notificationModal.user && (
        <ConfirmDialog
          open={notificationModal.open}
          title="Send Push Notification"
          message={<p>Sending notification to: <strong>{getUserIdentity(notificationModal.user)}</strong>{notificationModal.user.email && getUserFullName(notificationModal.user) !== 'N/A' && <> (<span>{getUserFullName(notificationModal.user)}</span>)</>}</p>}
          confirmLabel="Send Notification"
          confirmTitle={`Send this title and message to ${getUserIdentity(notificationModal.user)}`}
          submitting={sendingNotification}
          allowCancelWhileSubmitting
          error={notificationResult?.success === false ? notificationResult.message : null}
          onConfirm={sendNotification}
          onCancel={closeNotificationModal}
        >
          <div className="space-y-4">
            <div>
              <Label htmlFor="notification-title">Notification Title</Label>
              <Input id="notification-title" name="title" value={notification.title} onChange={handleNotificationChange} placeholder="Enter notification title" title="Title shown in the recipient's push notification" aria-invalid={missingNotification.title || undefined} aria-describedby={`notification-title-help${missingNotification.title ? ' notification-title-error' : ''}`} />
              <p id="notification-title-help" className="text-xs text-muted-foreground">Shown as the notification heading.</p>
              {missingNotification.title && <p id="notification-title-error" role="alert" className="text-sm text-destructive">Enter a notification title.</p>}
            </div>
            <div>
              <Label htmlFor="notification-body">Notification Message</Label>
              <Textarea id="notification-body" name="body" value={notification.body} onChange={handleNotificationChange} rows={3} placeholder="Enter notification message" title="Message shown in the recipient's push notification" aria-invalid={missingNotification.body || undefined} aria-describedby={`notification-body-help${missingNotification.body ? ' notification-body-error' : ''}`} />
              <p id="notification-body-help" className="text-xs text-muted-foreground">Shown below the title on the recipient's phone.</p>
              {missingNotification.body && <p id="notification-body-error" role="alert" className="text-sm text-destructive">Enter a notification message.</p>}
            </div>
            {notificationResult?.success && <p role="status" className="rounded-md border border-status-success-border bg-status-success p-3 text-status-success-foreground">{notificationResult.message}</p>}
          </div>
        </ConfirmDialog>
      )}
    </div>
  );
};

export default AppUsers;
